import { useLanguage } from '../hooks/useLanguage';
import { useState, useEffect, useRef } from 'react';

export default function PurchaseForm() {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const [formData, setFormData] = useState({
    storeName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    facebookPage: '',
    paymentPlan: 'monthly',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [clientId, setClientId] = useState(null);
  // Holds the in-flight lead-creation request started on phone blur, so submit
  // can await it instead of firing a second POST (which created duplicates).
  const creationPromiseRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.phone.trim()) newErrors.phone = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePhoneBlur = async () => {
    if (!formData.phone.trim() || clientId || creationPromiseRef.current) return;
    const BACKOFFICE_URL = process.env.REACT_APP_BACKOFFICE_URL || 'https://backoffice.youshop.pro/api';
    creationPromiseRef.current = (async () => {
      try {
        const res = await fetch(`${BACKOFFICE_URL}/ShopRequest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phone: formData.phone,
            storeName: formData.storeName || '',
            firstName: formData.firstName || '',
            lastName: formData.lastName || '',
            email: formData.email || '',
            address: formData.address || '',
            facebookPage: formData.facebookPage || '',
            paymentPlan: formData.paymentPlan || '',
          }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data?.id) {
            setClientId(data.id);
            return data.id;
          }
        }
      } catch (err) {
        console.error('Error creating client on phone blur:', err);
      }
      return null;
    })();
    await creationPromiseRef.current;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (submitting) return;

    setSubmitting(true);
    try {
      const BACKOFFICE_URL = process.env.REACT_APP_BACKOFFICE_URL || 'https://backoffice.youshop.pro/api';

      // If a lead was already (or is being) created on phone blur, reuse its id
      // and update it instead of creating a duplicate.
      let existingId = clientId;
      if (!existingId && creationPromiseRef.current) {
        existingId = await creationPromiseRef.current;
      }

      const payload = {
        storeName: formData.storeName,
        firstName: formData.firstName || null,
        lastName:  formData.lastName || null,
        email:     formData.email || null,
        phone:     formData.phone,
        address:   formData.address || null,
        facebookPage: formData.facebookPage || null,
        paymentPlan: formData.paymentPlan || null,
      };

      let res;
      if (existingId) {
        res = await fetch(`${BACKOFFICE_URL}/ShopRequest/${existingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${BACKOFFICE_URL}/ShopRequest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      if (!res.ok && res.status !== 409) {
        throw new Error('Server error');
      }

      // Fire Facebook Pixel Purchase event
      if (window.fbq) {
        window.fbq('track', 'Purchase', {
          value: 99.00,
          currency: 'TND',
          content_name: formData.storeName,
          content_type: 'product',
        });
      }

      setSuccess(true);
    } catch (err) {
      console.error('Form submission error:', err);
      setSuccess(true);
    } finally {
      setSubmitting(false);
    }
  };

  const fields = t('form.fields');

  const inputClass = (field) =>
    `w-full px-4 py-3.5 bg-white border rounded-xl text-navy-800 placeholder-navy-300 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-all ${
      errors[field] ? 'border-red-400' : 'border-gray-200'
    }`;

  return (
    <section ref={sectionRef} className="section-padding bg-white relative overflow-hidden">

      <div className="w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left - Info */}
          <div className="reveal">
            <span className="inline-block px-4 py-1.5 rounded-full bg-brand-50 border border-brand-200 text-brand-600 text-sm font-semibold tracking-wider mb-6">
              {t('form.badge')}
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold text-navy-800">
              {t('form.title')}{' '}
              <span className="text-brand-500">{t('form.titleHighlight')}</span>
            </h2>
            <p className="mt-6 text-xl text-navy-400">
              {t('form.subtitle')}
            </p>

            {/* Benefits */}
            <div className="mt-12 space-y-5">
              {['⚡', '🔒', '📱', '🚚', '💳'].map((icon, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-navy-50 flex items-center justify-center">
                    <span className="text-xl">{icon}</span>
                  </div>
                  <span className="text-navy-600 text-lg">{t(`form.benefits.${index}`)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Form */}
          <div className="reveal relative" style={{ transitionDelay: '200ms' }}>
            <div id="get-started" style={{ position: 'absolute', top: '-80px' }} aria-hidden="true" />
            <div className="bg-navy-50/50 border border-gray-200 rounded-3xl p-8 lg:p-10 relative">
              {success ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
                    <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-navy-700 text-lg font-medium">{t('form.success')}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Full Name */}
                  <div>
                    <span id="store-form" style={{ display: 'block', position: 'relative', top: '-80px', visibility: 'hidden' }} aria-hidden="true" />
                    <label className="block text-sm font-medium text-navy-700 mb-2">
                      {t('form.fullName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder={t('form.fullNamePlaceholder')}
                      className={inputClass('firstName')}
                    />
                  </div>

                  {/* Store Name */}
                  <div>
                    <label className="block text-sm font-medium text-navy-700 mb-2">
                      {t('form.fields.storeName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="facebookPage"
                      value={formData.facebookPage}
                      onChange={handleChange}
                      placeholder={t('form.fields.storeNamePlaceholder')}
                      className={inputClass('facebookPage')}
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-semibold text-navy-800 mb-2">
                      {fields?.phone || t('form.fields.phone')} <span className="text-red-500">*</span>
                    </label>
                    <div
                      className={`phone-attract group flex items-center gap-2 rounded-2xl border-2 bg-white px-3 transition-all focus-within:border-brand-500 focus-within:ring-4 focus-within:ring-brand-500/20 ${
                        errors.phone ? 'border-red-400' : 'border-brand-200'
                      }`}
                    >
                      {/* Country prefix with flag */}
                      <span className="flex shrink-0 items-center gap-1.5 border-r border-gray-200 py-3 pr-3 text-navy-800">
                        <span className="text-xl leading-none">🇹🇳</span>
                        <span className="text-sm font-bold tabular-nums">+216</span>
                      </span>
                      {/* Phone icon */}
                      <span className="phone-ring text-brand-500">
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M6.62 10.79a15.53 15.53 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24 11.36 11.36 0 0 0 3.57.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2z" />
                        </svg>
                      </span>
                      <input
                        type="tel"
                        name="phone"
                        id="phone-input"
                        value={formData.phone}
                        onChange={handleChange}
                        onBlur={handlePhoneBlur}
                        placeholder="XX XXX XXX"
                        inputMode="tel"
                        autoComplete="tel"
                        className="w-full bg-transparent py-3.5 text-lg font-semibold tracking-wide text-navy-900 placeholder-navy-300 focus:outline-none"
                      />
                    </div>
                    <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-brand-600">
                      <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      {t('form.phoneReassurance')}
                    </p>
                  </div>

                  {/* Payment Plan - Fixed at 99 TND */}
                  <div>
                    <label className="block text-sm font-medium text-navy-700 mb-3">
                      {t('form.paymentPlan.label')}
                    </label>
                    <div className="p-4 rounded-xl border border-brand-500 bg-brand-50/50 ring-2 ring-brand-500/20">
                      <div className="font-semibold text-navy-800">{t('form.paymentPlan.monthly')}</div>
                    </div>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full btn-primary !py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {t('form.submitting')}
                      </span>
                    ) : (
                      t('form.submit')
                    )}
                  </button>

                  <p className="text-xs text-navy-400 text-center">
                    {t('form.terms')}
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
